import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Eye, ScrollText, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { ouvrirPdf } from '../reglement-interieur/SignerReglementDialog';

const STATUTS = {
  signe: { label: 'Signé', cls: 'bg-green-500 text-white' },
  a_signer: { label: 'En attente de signature', cls: 'bg-amber-500 text-white' },
  non_envoye: { label: 'Non envoyé', cls: 'bg-slate-200 text-slate-600' },
};
const dateFr = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '-');

// Fiche stagiaire (centre) : règlement intérieur envoyé / signé, juste après la convention de formation
const ReglementInterieurSection = ({ stagiaireId, stagiaire, peutEnvoyer }) => {
  const [r, setR] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const charger = useCallback(() => {
    axios.get(`/reglement-interieur/stagiaire/${stagiaireId}`).then(({ data }) => setR(data)).catch(() => setR(null));
  }, [stagiaireId]);
  useEffect(() => { charger(); }, [charger]);
  if (!r) return null;

  const nom = `${(stagiaire?.user_info?.nom || '').replace(/\s+/g, '_')}_${(stagiaire?.user_info?.prenom || '').replace(/\s+/g, '_')}`.replace(/^_|_$/g, '') || 'Stagiaire';
  const statut = STATUTS[r.statut];
  const envoyer = async () => {
    setEnvoi(true);
    try {
      await axios.post('/reglement-interieur/envoyer', { stagiaire_ids: [stagiaireId] });
      toast.success('Règlement intérieur envoyé (e-mail + notification)');
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Envoi impossible');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-white border-2 border-orange-200 rounded-lg shadow-sm" data-testid="stagiaire-reglement-section">
      <div className="bg-orange-50 px-6 py-4 border-b border-orange-200 flex items-center justify-between gap-3">
        <h2 className="text-lg font-display font-semibold text-[#0F172A] flex items-center gap-2"><ScrollText className="h-5 w-5 text-orange-500" /> Règlement intérieur</h2>
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${statut.cls}`} data-testid="stagiaire-reglement-statut">{statut.label}</span>
      </div>
      <div className="p-6 space-y-3">
        <div className="bg-orange-50/60 rounded-lg p-3 border border-orange-100 text-sm space-y-1" data-testid="stagiaire-reglement-info">
          <p><span className="text-slate-500">Envoyé le :</span> <span className="font-medium">{dateFr(r.envoye_le)}</span>{r.nb_envois > 1 && <span className="text-slate-400"> ({r.nb_envois} envois)</span>}</p>
          {r.nb_relances > 0 && <p><span className="text-slate-500">Relances automatiques :</span> <span className="font-medium">{r.nb_relances} (dernière le {dateFr(r.derniere_relance)})</span></p>}
          <p><span className="text-slate-500">Signé le :</span> <span className="font-medium">{dateFr(r.signe_le)}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          {r.statut === 'signe' ? (
            <>
              <Button variant="outline" size="sm" onClick={() => ouvrirPdf(`/reglement-interieur/signe/${stagiaireId}`, `${nom}_Reglement_interieur_signe.pdf`)} data-testid="stagiaire-reglement-voir" title="Prévisualiser"><Eye className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => ouvrirPdf(`/reglement-interieur/signe/${stagiaireId}`, `${nom}_Reglement_interieur_signe.pdf`, 'download')} data-testid="stagiaire-reglement-telecharger"><Download className="h-4 w-4 mr-1" /> Télécharger</Button>
            </>
          ) : peutEnvoyer && (
            <Button size="sm" onClick={envoyer} disabled={envoi} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="stagiaire-reglement-envoyer">
              <Send className="h-4 w-4 mr-1" /> {r.statut === 'a_signer' ? 'Renvoyer' : 'Envoyer'} le règlement
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => ouvrirPdf('/reglement-interieur/pdf', 'Reglement_interieur_FISL.pdf')} data-testid="stagiaire-reglement-vierge">Règlement vierge</Button>
        </div>
      </div>
    </div>
  );
};

export default ReglementInterieurSection;
